/**
 * PointsService - Core business logic for points management
 */


export class PointsService {
  constructor(pointsRepo) {
    this.pointsRepo = pointsRepo;
  }

  /**
   * Add points (for recognitions)
   * @param {Object} options - Points options
   * @returns {Promise<void>}
   */
  async addPoints({
    studentId,
    studentName,
    classId,
    className,
    houseId,
    houseName,
    amount,
    type = 'recognition',
    sourceId,
    valueId = null,
    valueName = null,
    schoolId
  }) {
    if (amount <= 0) {
      throw new Error('Points amount must be positive');
    }

    await this.pointsRepo.updatePoints({
      studentId,
      studentName,
      classId,
      className,
      houseId,
      houseName,
      amount,
      type,
      valueId,
      valueName,
      schoolId
    });
  }

  /**
   * Deduct points (for violations)
   * @param {Object} options - Points options
   * @returns {Promise<void>}
   */
  async deductPoints({
    studentId,
    studentName,
    classId,
    className,
    houseId,
    houseName,
    amount,
    type = 'violation',
    sourceId,
    valueId = null,
    valueName = null,
    schoolId
  }) {
    if (amount <= 0) {
      throw new Error('Deduction amount must be positive');
    }

    await this.pointsRepo.updatePoints({
      studentId,
      studentName,
      classId,
      className,
      houseId,
      houseName,
      amount,
      type: 'violation', // Force type to violation for deductions
      valueId,
      valueName,
      schoolId
    });
  }

  /**
   * Reverse a previous violation points deduction (for mention deletion/voiding)
   * @param {Object} options - Same shape as deductPoints
   * @returns {Promise<void>}
   */
  async reverseViolationPoints({
    studentId,
    studentName,
    classId,
    className,
    houseId,
    houseName,
    amount,
    sourceId,
    valueId = null,
    valueName = null,
    schoolId
  }) {
    if (amount <= 0) {
      throw new Error('Reversal amount must be positive');
    }

    await this.pointsRepo.reverseViolationPoints({
      studentId,
      studentName,
      classId,
      className,
      houseId,
      houseName,
      amount,
      valueId,
      valueName,
      schoolId
    });
  }

  /**
   * Get points for a house
   * @param {string} houseId - House ID
   * @returns {Promise<Object>} House points
   */
  async getHousePoints(houseId) {
    const points = await this.pointsRepo.getHousePoints(houseId);

    // Return default structure if no points yet
    if (!points) {
      return {
        houseId,
        total: 0,
        positive: 0,
        negative: 0,
        lastUpdated: null
      };
    }

    return points;
  }

  /**
   * Get points for a class
   * @param {string} classId - Class ID
   * @returns {Promise<Object>} Class points
   */
  async getClassPoints(classId) {
    const points = await this.pointsRepo.getClassPoints(classId);

    // Return default structure if no points yet
    if (!points) {
      return {
        classId,
        total: 0,
        positive: 0,
        negative: 0,
        houseBreakdown: {},
        lastUpdated: null
      };
    }

    return points;
  }

  /**
   * Get points for a student
   * @param {string} studentId - Student ID
   * @returns {Promise<Object>} Student points (never rejects — returns zeroed default on error)
   */
  async getStudentPoints(studentId) {
    try {
      const points = await this.pointsRepo.getStudentPoints(studentId);

      // Return default structure if no points yet
      if (!points) {
        return {
          studentId,
          total: 0,
          positive: 0,
          negative: 0,
          byValue: {},
          lastUpdated: null
        };
      }

      return points;
    } catch (error) {
      console.warn('[PointsService] getStudentPoints failed:', error.code, error.message);
      return {
        studentId,
        total: 0,
        positive: 0,
        negative: 0,
        byValue: {},
        lastUpdated: null
      };
    }
  }

  /**
   * Get house leaderboard for a school
   * @param {string} schoolId - School ID
   * @returns {Promise<Array>} Array of houses sorted by points
   */
  async getHouseLeaderboard(schoolId) {
    return await this.pointsRepo.getHouseLeaderboard(schoolId);
  }

  /**
   * Get class leaderboard for a school
   * @param {string} schoolId - School ID
   * @returns {Promise<Array>} Array of classes sorted by points
   */
  async getClassLeaderboard(schoolId) {
    return await this.pointsRepo.getClassLeaderboard(schoolId);
  }

  /**
   * Get student leaderboard
   * @param {Object} options - Query options
   * @returns {Promise<Array>} Array of students sorted by points
   */
  async getStudentLeaderboard(options = {}) {
    return await this.pointsRepo.getStudentLeaderboard(options);
  }

  /**
   * Get top performers by value
   * @param {string} schoolId - School ID
   * @param {string} valueId - Value ID
   * @param {number} limit - Number of students to return
   * @returns {Promise<Array>} Top students for this value
   */
  async getTopPerformersByValue(schoolId, valueId, limit = 10) {
    // Get all students with points in this value
    const students = await this.pointsRepo.getStudentLeaderboard({ schoolId });

    // Filter and sort by specific value
    const withValue = students
      .filter(s => s.byValue && s.byValue[valueId])
      .map(s => ({
        ...s,
        valuePoints: s.byValue[valueId].points,
        valueCount: s.byValue[valueId].count
      }))
      .sort((a, b) => b.valuePoints - a.valuePoints);

    return withValue.slice(0, limit);
  }

  /**
   * Reset points for a period
   * @param {string} schoolId - School ID
   * @param {string} period - Period identifier
   * @param {string} scope - Scope to reset ('all', 'houses', 'classes', 'students')
   * @returns {Promise<void>}
   */
  async resetPeriod(schoolId, period, scope = 'all') {
    await this.pointsRepo.resetPeriod(schoolId, period, scope);
  }

  /**
   * Recalculate points from source documents
   * @param {string} schoolId - School ID
   * @param {string} scope - 'student', 'class', or 'house'
   * @param {string} scopeId - The ID to recalculate
   * @returns {Promise<Object>} Recalculated totals
   */
  async recalculatePoints(schoolId, scope, scopeId) {
    return await this.pointsRepo.recalculatePoints(schoolId, scope, scopeId);
  }

  /**
   * Get points summary for multiple entities
   * @param {Object} options - Query options
   * @returns {Promise<Object>} Points summary
   */
  async getPointsSummary({ schoolId, houseIds = [], classIds = [], studentIds = [] }) {
    const summary = {
      houses: {},
      classes: {},
      students: {},
      school: {
        total: 0,
        positive: 0,
        negative: 0
      }
    };

    // Fetch house points
    for (const houseId of houseIds) {
      const points = await this.getHousePoints(houseId);
      summary.houses[houseId] = points;
      summary.school.total += points.total;
      summary.school.positive += points.positive;
      summary.school.negative += points.negative;
    }

    // Fetch class points
    for (const classId of classIds) {
      const points = await this.getClassPoints(classId);
      summary.classes[classId] = points;
    }

    // Fetch student points
    for (const studentId of studentIds) {
      const points = await this.getStudentPoints(studentId);
      summary.students[studentId] = points;
    }

    return summary;
  }

  /**
   * Subscribe to house points updates
   * @param {string} houseId - House ID
   * @param {Function} callback - Callback function
   * @returns {Function} Unsubscribe function
   */
  subscribeToHousePoints(houseId, callback) {
    // Use the repository's subscribe method for real-time updates
    return this.pointsRepo.subscribeToDocument(
      houseId,
      (data) => {
        callback(data || {
          houseId,
          total: 0,
          positive: 0,
          negative: 0,
          lastUpdated: null
        });
      },
      'houses' // Subcollection name
    );
  }

  /**
   * Subscribe to class points updates
   * @param {string} classId - Class ID
   * @param {Function} callback - Callback function
   * @returns {Function} Unsubscribe function
   */
  subscribeToClassPoints(classId, callback) {
    return this.pointsRepo.subscribeToDocument(
      classId,
      (data) => {
        callback(data || {
          classId,
          total: 0,
          positive: 0,
          negative: 0,
          houseBreakdown: {},
          lastUpdated: null
        });
      },
      'classes' // Subcollection name
    );
  }
}